import axios from "axios";
import {
  CONFIG,
  PATIENT_CONFIGS,
} from "../../utility/constants/common-constants";

const ALLOWED_HOSTNAMES = process.env.REACT_APP_ALLOWED_HOSTNAMES
  ? process.env.REACT_APP_ALLOWED_HOSTNAMES.split(",").map((hostname) =>
      hostname.trim()
    )
  : [];

export const sendPatientTokenData = async (data: {
  PatientFhirId: string;
  AccessToken: string;
  ExpiresInSecond: number;
}) => {
  try {
    const response = await axios.post(`${CONFIG.baseURL}/api/v1/Auth`, data, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Failed to send patient token data:", error);
    throw error;
  }
};

// Utility to wait for a specified duration
export const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

export const fetchSmartConfig = async (iss: string): Promise<string | null> => {
  try {
    const inputUrl = new URL(iss);
    if (!ALLOWED_HOSTNAMES.includes(inputUrl.origin)) {
      throw new Error("Invalid or untrusted 'iss' parameter.");
    }

    // Determine the correct SMART configuration URL based on SSO mode
    const smartConfigUrl = `${inputUrl.href}/.well-known/smart-configuration`;

    const response = await axios.get(smartConfigUrl, {
      headers: { Accept: "application/json" },
    });
    const { authorization_endpoint, token_endpoint } = response.data;

    if (authorization_endpoint?.includes("mychart")) {
      throw new Error(
        "Authorization endpoint points to MyChart login. This is incorrect."
      );
    }

    localStorage.setItem("smartConfigDataTokenEndpoint", token_endpoint);
    return authorization_endpoint;
  } catch (err) {
    console.error("Error fetching SMART configuration:", err);
    throw new Error("Failed to fetch SMART configuration.");
  }
};

export const initiateOAuthFlow = async (
  iss: string,
  launch: string
): Promise<void> => {
  const authorizationEndpoint = await fetchSmartConfig(iss);
  if (!authorizationEndpoint)
    throw new Error("Authorization endpoint could not be retrieved.");

  const state = crypto.randomUUID();
  localStorage.setItem(PATIENT_CONFIGS.LOCAL_STATE_KEY || "", state);

  const authUrl = new URL(authorizationEndpoint);
  authUrl.searchParams.append("response_type", "code");
  authUrl.searchParams.append(
    "client_id",
    PATIENT_CONFIGS.FHIR_CLIENT_ID || ""
  );
  authUrl.searchParams.append(
    "redirect_uri",
    PATIENT_CONFIGS.REDIRECT_URI || ""
  );
  authUrl.searchParams.append("launch", launch);
  authUrl.searchParams.append("scope", "launch");
  authUrl.searchParams.append("state", state);
  authUrl.searchParams.append("aud", iss);

  console.log("Redirecting to:", authUrl.toString());
  window.location.href = authUrl.toString();
};

export const exchangeCodeForToken = async (code: string) => {
  try {
    const tokenEndpoint = localStorage.getItem("smartConfigDataTokenEndpoint");
    if (!tokenEndpoint) throw new Error("Token endpoint not available.");

    const response = await axios.post(
      tokenEndpoint,
      new URLSearchParams({
        grant_type: "authorization_code",
        code,
        redirect_uri: PATIENT_CONFIGS.REDIRECT_URI || "",
        client_id: PATIENT_CONFIGS.FHIR_CLIENT_ID || "",
      }),
      { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
    );

    return response.data;
  } catch (err) {
    console.error("Error exchanging code for token:", err);
    throw new Error("Failed to exchange authorization code for token.");
  }
};

// Utility function to validate authorization code
export const isValidAuthorizationCode = (code: string): boolean => {
  // Example: Check that the code is alphanumeric and of a reasonable length
  const codePattern = /^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+$/;
  return codePattern.test(code);
};

// Utility function to validate issuer (iss)
export const isValidIssuer = (iss: string): boolean => {
  try {
    const parsedUrl = new URL(iss);
    return ALLOWED_HOSTNAMES.includes(parsedUrl.origin);
  } catch {
    return false;
  }
};

// Utility function to validate launch parameter (basic example)
export const isValidLaunch = (launch: string): boolean => {
  // Example: Ensure it's a base64-encoded JWT
  try {
    const decodedLaunch = JSON.parse(atob(launch.split(".")[1]));
    return typeof decodedLaunch === "object" && decodedLaunch !== null;
  } catch {
    return false;
  }
};
